/**
 * Feature 3: Abandoned Cart Analysis
 * Shows products most frequently added to cart but not purchased
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  MenuItem,
  LinearProgress,
} from '@mui/material';
import { ShoppingCart } from '@mui/icons-material';
import apiService from '../services/apiService';

const AbandonedCarts = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    fetchData();
  }, [limit]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAbandonedCarts(limit);
      setData(response.products);
    } catch (err) {
      setError('Failed to load abandoned cart data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatPrice = (price) => {
    return `$${price.toFixed(2)}`;
  };

  const getAbandonmentColor = (rate) => {
    if (rate >= 70) return 'error';
    if (rate >= 50) return 'warning';
    return 'default';
  };

  if (loading) {
    return (
      <Card>
        <CardContent style={{ textAlign: 'center', padding: '40px' }}>
          <CircularProgress />
          <Typography variant="body2" style={{ marginTop: '16px' }}>
            Loading abandoned cart data...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <Typography variant="h5" gutterBottom>
              Abandoned Cart Analysis
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Products most frequently abandoned in cart
            </Typography>
          </div>
          <TextField
            select
            label="Show"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            size="small"
            style={{ minWidth: '100px' }}
          >
            <MenuItem value={10}>Top 10</MenuItem>
            <MenuItem value={20}>Top 20</MenuItem>
            <MenuItem value={50}>Top 50</MenuItem>
            <MenuItem value={100}>Top 100</MenuItem>
          </TextField>
        </div>

        {data && data.length > 0 ? (
          <>
            {/* Summary Stats */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1', minWidth: '150px', padding: '12px', backgroundColor: '#fff3e0', borderRadius: '8px' }}>
                <Typography variant="caption" color="textSecondary">
                  Total Abandoned Carts
                </Typography>
                <Typography variant="h6">
                  {formatNumber(data.reduce((sum, p) => sum + p.abandonment_count, 0))}
                </Typography>
              </div>
              <div style={{ flex: '1', minWidth: '150px', padding: '12px', backgroundColor: '#fff3e0', borderRadius: '8px' }}>
                <Typography variant="caption" color="textSecondary">
                  Avg Abandonment Rate
                </Typography>
                <Typography variant="h6">
                  {(data.reduce((sum, p) => sum + p.abandonment_rate, 0) / data.length).toFixed(2)}%
                </Typography>
              </div>
              <div style={{ flex: '1', minWidth: '150px', padding: '12px', backgroundColor: '#fff3e0', borderRadius: '8px' }}>
                <Typography variant="caption" color="textSecondary">
                  Top Product
                </Typography>
                <Typography variant="h6">
                  {formatNumber(data[0]?.abandonment_count)}
                </Typography>
              </div>
            </div>

            {/* Abandoned Carts Table */}
            <TableContainer component={Paper} style={{ maxHeight: '600px' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 'bold' }}>Rank</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Product ID</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Brand</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Category</TableCell>
                    <TableCell align="right" style={{ fontWeight: 'bold' }}>Price</TableCell>
                    <TableCell align="right" style={{ fontWeight: 'bold' }}>Cart Adds</TableCell>
                    <TableCell align="right" style={{ fontWeight: 'bold' }}>Purchases</TableCell>
                    <TableCell align="right" style={{ fontWeight: 'bold' }}>Abandoned</TableCell>
                    <TableCell align="center" style={{ fontWeight: 'bold' }}>Abandonment Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((product, index) => (
                    <TableRow key={product.product_id} hover>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {index + 1}
                          {index < 3 && <ShoppingCart style={{ color: '#FF9800', fontSize: '16px' }} />}
                        </div>
                      </TableCell>
                      <TableCell>{product.product_id}</TableCell>
                      <TableCell>{product.brand}</TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontSize: '0.85rem' }}>
                          {product.category}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatPrice(product.price)}</TableCell>
                      <TableCell align="right">{formatNumber(product.cart_adds)}</TableCell>
                      <TableCell align="right">{formatNumber(product.purchases)}</TableCell>
                      <TableCell align="right">
                        <strong>{formatNumber(product.abandonment_count)}</strong>
                      </TableCell>
                      <TableCell align="center">
                        <div>
                          <Chip
                            label={`${product.abandonment_rate.toFixed(2)}%`}
                            color={getAbandonmentColor(product.abandonment_rate)}
                            size="small"
                          />
                          <LinearProgress
                            variant="determinate"
                            value={product.abandonment_rate}
                            style={{ marginTop: '4px', height: '4px', borderRadius: '2px' }}
                            color={getAbandonmentColor(product.abandonment_rate) === 'error' ? 'error' : 'warning'}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Insights */}
            <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
              <Typography variant="subtitle2" gutterBottom>
                💡 Key Insights
              </Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>
                  <Typography variant="body2">
                    Top product has {formatNumber(data[0]?.abandonment_count)} abandoned carts 
                    ({data[0]?.abandonment_rate.toFixed(2)}% abandonment rate)
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Average price of abandoned products: {formatPrice(data.reduce((sum, p) => sum + p.price, 0) / data.length)}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    {data.filter(p => p.abandonment_rate > 70).length} products have &gt;70% abandonment rate
                  </Typography>
                </li>
              </ul>
            </div>
          </>
        ) : (
          <Alert severity="info">No abandoned cart data found</Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default AbandonedCarts;
